/**
 * Application (module) search.
 *
 * SECURITY: this filters a list that has ALREADY been scoped by
 * `moduleService.listModulesForUser`, which is the app's access boundary. It is
 * a narrowing operation over what the caller may already see — it never reads
 * the full module store. A module the user is not entitled to therefore cannot
 * surface through search, and none of its name, description, id, or
 * configuration is ever consulted.
 *
 * Passing an unscoped list here would be a bug; callers must hand over
 * `visibleModules`, never `listAllModules()`.
 */

import type { AppModule } from '../types';
import { getDashboardConfig, isDashboard } from '../dashboard/services/dashboardService';

/** Normalises for accent-insensitive, case-insensitive matching. */
function normalise(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** The searchable text for a module: name, title, type, and description. */
function searchableText(module: AppModule): string {
  const parts: string[] = [module.title ?? '', module.name ?? ''];

  // Application type, so "dashboard" or "map" are useful queries.
  parts.push(isDashboard(module) ? 'dashboard' : 'map app map');

  // Dashboards carry an admin-authored description worth searching.
  if (isDashboard(module)) {
    try {
      parts.push(getDashboardConfig(module).description ?? '');
    } catch {
      // A malformed config must not break search.
    }
  }

  // Tools double as tags for map apps ("measure", "print", …).
  if (Array.isArray(module.tools)) parts.push(module.tools.join(' '));

  parts.push(module.built ? 'live published' : 'draft');

  return normalise(parts.filter(Boolean).join(' '));
}

export const applicationSearchService = {
  /**
   * Filters the user's visible applications.
   * An empty query returns the list unchanged, so the caller can bind this
   * directly without special-casing the idle state.
   */
  filter(visibleModules: AppModule[], query: string): AppModule[] {
    const needle = normalise(query);
    if (needle === '') return visibleModules;

    // Every whitespace-separated term must match somewhere, so "gas dashboard"
    // narrows rather than widening the way a plain OR would.
    const terms = needle.split(/\s+/).filter(Boolean);

    return visibleModules.filter((module) => {
      const haystack = searchableText(module);
      return terms.every((term) => haystack.includes(term));
    });
  },
};
